// Captures traffic source (UTM parameters + document.referrer) on the FIRST
// visit and persists it in localStorage so the attribution survives if the
// visitor browses the site before submitting the form.
//
// Persistence is one-shot: once we have a snapshot for this browser, we do
// NOT overwrite it on subsequent visits (otherwise a click on an internal
// link would erase the original UTM). If the visitor arrives later through a
// distinct campaign we refresh — see UTM_KEYS logic below.

const STORAGE_KEY = "sircs_attribution_v1";
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content"];

function readUrlParams() {
  if (typeof window === "undefined") return {};
  try {
    const p = new URLSearchParams(window.location.search);
    const out = {};
    for (const k of UTM_KEYS) {
      const v = p.get(k);
      if (v && v.trim()) out[k] = v.trim().slice(0, 200);
    }
    return out;
  } catch (_) {
    return {};
  }
}

/**
 * Capture (once) and return the attribution snapshot for this browser.
 * Called eagerly from Landing so we don't miss the referrer on later hops.
 */
export function captureAttribution() {
  if (typeof window === "undefined") return null;
  let existing = null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) existing = JSON.parse(raw);
  } catch (_) {
    existing = null;
  }

  const urlParams = readUrlParams();
  // If this visit brings NEW utm_source, refresh the snapshot (new campaign).
  if (existing && !urlParams.utm_source) {
    return existing;
  }

  const snapshot = {
    utm_source: urlParams.utm_source || existing?.utm_source || null,
    utm_medium: urlParams.utm_medium || existing?.utm_medium || null,
    utm_campaign: urlParams.utm_campaign || existing?.utm_campaign || null,
    utm_content: urlParams.utm_content || existing?.utm_content || null,
    referrer:
      (typeof document !== "undefined" && document.referrer) ||
      existing?.referrer ||
      null,
    captured_at: new Date().toISOString(),
  };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  } catch (_) {
    /* ignore quota / private mode */
  }
  return snapshot;
}

/** Read the persisted attribution snapshot (no mutation). */
export function readAttribution() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

/**
 * Build the payload piece that goes to the backend. Applies fallbacks:
 *   - utm_source empty → "organico"
 *   - referrer empty → "directo"
 * These defaults live client-side too so the admin table shows them even
 * when the backend has legacy rows without defaults.
 */
export function attributionPayload() {
  const a = readAttribution() || captureAttribution() || {};
  return {
    utm_source: a.utm_source || "organico",
    utm_medium: a.utm_medium || null,
    utm_campaign: a.utm_campaign || null,
    utm_content: a.utm_content || null,
    referrer: a.referrer || "directo",
  };
}
